// mui
import { createTheme } from '@mui/material/styles';

// internal
import { PopperComponent, ListboxComponent } from '../customize/my-autocomplete.jsx';

const LightTheme = createTheme({
    palette: {
        type: 'light',
        primary: {
            main: 'rgb(57, 42, 72)', // Matte Purple
            light: 'rgb(87, 72, 102)',
            dark: 'rgb(27, 12, 42)',
            bright: 'rgb(117, 102, 132)',
        },
        secondary: {
            main: 'rgb(81, 81, 81)',
            light: 'rgb(111, 111, 111)',
            dark: 'rgb(51, 51, 51)', // Dark Charcoal
        },
        // typography
        text: {
            primary: 'rgb(35, 35, 35)', // Young Night
            secondary: 'rgba(35, 35, 35, 0.8)',
            disabled: 'rgba(35, 35, 35, 0.38)',
        },
        // background
        background: {
            paper: 'rgb(253, 243, 213)', // Satin Cream White
            default: 'rgb(255, 247, 209)', // Notes Color
        },
        // divider
        divider: 'rgb(57, 42, 72)', // Matte Purple
        // buttons
        action: {
            active: 'rgba(57, 42, 72, 0.54)',
            hover: 'rgba(57, 42, 72, 0.06)',
            hoverOpacity: 0.06,
            selected: 'rgba(57, 42, 72, 0.1)',
            selectedOpacity: 0.1,
            disabled: 'rgba(57, 42, 72, 0.26)',
            disabledBackground: 'rgba(57, 42, 72, 0.12)',
            disabledOpacity: 0.38,
            focus: 'rgba(57, 42, 72, 0.12)',
            focusOpacity: 0.12,
            activatedOpacity: 0.12,
            activeChannel: '0 0 0',
            selectedChannel: '0 0 0',
        },
        markdown: {
            code: 'rgba(57, 42, 72, 0.05)',
            th: 'rgb(57, 42, 72)',
            td: 'rgba(57, 42, 72, 0.25)',
        },
        scrollbar: {
            thumb: '#c8c8c8',
            thumbHover: '#ababab',
        },
    },
    components: {
        MuiTypography: {
            variants: [
                {
                    props: { variant: 'm_overline' },
                    style: { textTransform: 'uppercase' },
                },
                {
                    props: { variant: 'm_overline_lg' },
                    style: { textTransform: 'uppercase' },
                },
            ],
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    backgroundColor: 'rgb(253, 243, 213)',
                    color: 'rgb(57, 42, 72)',
                    border: '1px solid rgb(57, 42, 72)',
                    fontSize: '12px',
                },
                arrow: {
                    color: 'rgb(57, 42, 72)',
                },
            },
        },
        MuiTextField: {
            defaultProps: {
                variant: 'outlined',
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    '& fieldset': {
                        borderColor: 'rgb(57, 42, 72)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgb(87, 72, 102)',
                    },
                    '&.Mui-focused fieldset': {
                        borderColor: 'rgb(57, 42, 72) !important',
                    },
                    '&.Mui-error fieldset': {
                        borderColor: 'rgb(57, 42, 72) !important',
                    },
                },
                input: {
                    color: 'rgb(35, 35, 35)',
                },
            },
        },
        MuiFormLabel: {
            styleOverrides: {
                root: {
                    color: 'rgba(57, 42, 72, 1.0)',
                    '&.Mui-error': {
                        color: 'rgba(57, 42, 72, 1.0)',
                    },
                },
            },
        },
        MuiAutocomplete: {
            defaultProps: {
                PopperComponent: PopperComponent,
                ListboxComponent: ListboxComponent,
            },
            styleOverrides: {},
        },
    },
    typography: {
        m_display: {
            fontFamily: 'montserrat',
            fontWeight: 900,
            fontSize: '6rem',
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
        },
        m_logo: {
            fontFamily: 'montserrat',
            fontWeight: 800,
            fontSize: ' 3.75rem',
            lineHeight: 1.1,
            letterSpacing: '-0.015em',
        },
        m_titleXXL: {
            fontFamily: 'montserrat',
            fontWeight: 800,
            fontSize: '3rem',
            lineHeight: 1.15,
            letterSpacing: '-0.0125em',
        },
        m_titleXL: {
            fontFamily: 'montserrat',
            fontWeight: 800,
            fontSize: '2.5rem',
            lineHeight: 1.2,
            letterSpacing: '-0.01em',
        },
        m_titleL: {
            fontFamily: 'montserrat',
            fontWeight: 800,
            fontSize: '2rem',
            lineHeight: 1.2,
            letterSpacing: '-0.00833em',
        },
        m_titleM: {
            fontFamily: 'montserrat',
            fontWeight: 800,
            fontSize: '1.5rem',
            lineHeight: 1.25,
            letterSpacing: '-0.006em',
        },
        m_titleS: {
            fontFamily: 'montserrat',
            fontWeight: 800,
            fontSize: '1.25rem',
            lineHeight: 1.3,
            letterSpacing: '-0.004em',
        },
        m_username: {
            fontFamily: 'montserrat',
            fontWeight: 600,
            fontSize: '1rem',
            lineHeight: 1.5,
            letterSpacing: '0.00938em',
        },
        m_label: {
            fontFamily: 'montserrat',
            fontWeight: 600,
            fontSize: '0.9375rem',
            lineHeight: 1.5,
            letterSpacing: '0.01em',
        },
        m_body1: {
            fontFamily: 'montserrat',
            fontWeight: 500,
            fontSize: '1rem',
            lineHeight: 1.6,
            letterSpacing: '0.01em',
        },
        m_body2: {
            fontFamily: 'montserrat',
            fontWeight: 500,
            fontSize: '0.875rem',
            lineHeight: 1.6,
            letterSpacing: '0.01em',
        },
        m_caption: {
            fontFamily: 'montserrat',
            fontWeight: 600,
            fontSize: '0.75rem',
            lineHeight: 1.4,
            letterSpacing: '0.02em',
        },
        m_micro: {
            fontFamily: 'montserrat',
            fontWeight: 600,
            fontSize: '0.625rem',
            lineHeight: 1.3,
            letterSpacing: '0.03em',
        },
        m_tiny: {
            fontFamily: 'montserrat',
            fontWeight: 600,
            fontSize: '0.5rem',
            lineHeight: 1.3,
            letterSpacing: '0.03em',
        },
        m_nano: {
            fontFamily: 'montserrat',
            fontWeight: 600,
            fontSize: '0.4375rem',
            lineHeight: 1.3,
            letterSpacing: '0.03em',
        },
        m_overline: {
            fontFamily: 'montserrat',
            fontWeight: 700,
            fontSize: '0.75rem',
            lineHeight: 1.4,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
        },
        m_overline_lg: {
            fontFamily: 'montserrat',
            fontWeight: 800,
            fontSize: '0.9375rem',
            lineHeight: 1.4,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
        },
        m_badge: {
            fontFamily: 'montserrat',
            fontWeight: 800,
            fontSize: '0.6875rem',
            lineHeight: 1.2,
            letterSpacing: '0.02em',
        },
        m_dialog_title: {
            fontFamily: 'montserrat',
            fontWeight: 600,
            fontSize: '1.25rem',
            lineHeight: 1.6,
            letterSpacing: '0.0075em',
            color: 'rgba(57, 42, 72, 1.0)', // primary.main
        },
        m_dialog_content_text: {
            fontFamily: 'montserrat',
            fontWeight: 400,
            fontSize: '0.875rem',
            lineHeight: 1.43,
            letterSpacing: '0.01071em',
            color: 'rgba(81, 81, 81, 1.0)', // secondary.main
        },
    },
});

const DarkTheme = createTheme({
    palette: {
        type: 'dark',
        primary: {
            main: 'rgb(250, 231, 181)', // Banana Mania
            light: 'rgb(255, 251, 201)',
            dark: 'rgb(220, 201, 151)',
            bright: 'rgb(255, 251, 255)',
        },
        secondary: {
            main: 'rgb(211, 211, 211)', // Light Gray
            light: 'rgb(241, 241, 241)',
            dark: 'rgb(181, 181, 181)',
        },
        // typography
        text: {
            primary: 'rgb(255, 255, 244)', // White Gold
            secondary: 'rgb(255, 255, 244, 0.8)',
            disabled: 'rgba(255, 255, 244, 0.38)',
        },
        // background
        background: {
            paper: 'rgb(35, 35, 35)', // Young Night
            default: 'rgb(40, 40, 43)', // Matte Black
        },
        // divider
        divider: 'rgb(250, 231, 181)', // Banana Mania
        // buttons
        action: {
            active: 'rgba(250, 231, 181, 0.54)',
            hover: 'rgba(250, 231, 181, 0.06)',
            hoverOpacity: 0.06,
            selected: 'rgba(250, 231, 181, 0.1)',
            selectedOpacity: 0.1,
            disabled: 'rgba(250, 231, 181, 0.26)',
            disabledBackground: 'rgba(250, 231, 181, 0.12)',
            disabledOpacity: 0.38,
            focus: 'rgba(250, 231, 181, 0.12)',
            focusOpacity: 0.12,
            activatedOpacity: 0.12,
            activeChannel: '0 0 0',
            selectedChannel: '0 0 0',
        },
        markdown: {
            code: 'rgba(250, 231, 181, 0.05)',
            th: 'rgba(250, 231, 181, 1)',
            td: 'rgba(250, 231, 181, 0.25)',
        },
        scrollbar: {
            thumb: 'rgb(220, 201, 151)',
            thumbHover: 'rgb(250, 231, 181)',
        },
    },
    components: {
        MuiTypography: {
            variants: [
                {
                    props: { variant: 'm_overline' },
                    style: { textTransform: 'uppercase' },
                },
                {
                    props: { variant: 'm_overline_lg' },
                    style: { textTransform: 'uppercase' },
                },
            ],
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    backgroundColor: 'rgb(40, 40, 43)',
                    color: 'rgb(250, 231, 181)',
                    border: '1px solid rgb(250, 231, 181)',
                    fontSize: '12px',
                },
                arrow: {
                    color: 'rgb(250, 231, 181)',
                },
            },
        },
        MuiTextField: {
            defaultProps: {
                variant: 'outlined',
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    '& fieldset': {
                        borderColor: 'rgb(250, 231, 181)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgb(255, 251, 201)',
                    },
                    '&.Mui-focused fieldset': {
                        borderColor: 'rgb(250, 231, 181) !important',
                    },
                    '&.Mui-error fieldset': {
                        borderColor: 'rgb(250, 231, 181) !important',
                    },
                },
                input: {
                    color: 'rgb(255, 255, 244)',
                },
            },
        },
        MuiFormLabel: {
            styleOverrides: {
                root: {
                    color: 'rgb(250, 231, 181)',
                    '&.Mui-error': {
                        color: 'rgb(250, 231, 181)',
                    },
                },
            },
        },
        MuiAutocomplete: {
            defaultProps: {
                PopperComponent: PopperComponent,
                ListboxComponent: ListboxComponent,
            },
            styleOverrides: {},
        },
    },
    typography: {
        m_display: {
            fontFamily: 'montserrat',
            fontWeight: 900,
            fontSize: '6rem',
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
        },
        m_logo: {
            fontFamily: 'montserrat',
            fontWeight: 800,
            fontSize: ' 3.75rem',
            lineHeight: 1.1,
            letterSpacing: '-0.015em',
        },
        m_titleXXL: {
            fontFamily: 'montserrat',
            fontWeight: 800,
            fontSize: '3rem',
            lineHeight: 1.15,
            letterSpacing: '-0.0125em',
        },
        m_titleXL: {
            fontFamily: 'montserrat',
            fontWeight: 800,
            fontSize: '2.5rem',
            lineHeight: 1.2,
            letterSpacing: '-0.01em',
        },
        m_titleL: {
            fontFamily: 'montserrat',
            fontWeight: 800,
            fontSize: '2rem',
            lineHeight: 1.2,
            letterSpacing: '-0.00833em',
        },
        m_titleM: {
            fontFamily: 'montserrat',
            fontWeight: 800,
            fontSize: '1.5rem',
            lineHeight: 1.25,
            letterSpacing: '-0.006em',
        },
        m_titleS: {
            fontFamily: 'montserrat',
            fontWeight: 800,
            fontSize: '1.25rem',
            lineHeight: 1.3,
            letterSpacing: '-0.004em',
        },
        m_username: {
            fontFamily: 'montserrat',
            fontWeight: 600,
            fontSize: '1rem',
            lineHeight: 1.5,
            letterSpacing: '0.00938em',
        },
        m_label: {
            fontFamily: 'montserrat',
            fontWeight: 600,
            fontSize: '0.9375rem',
            lineHeight: 1.5,
            letterSpacing: '0.01em',
        },
        m_body1: {
            fontFamily: 'montserrat',
            fontWeight: 500,
            fontSize: '1rem',
            lineHeight: 1.6,
            letterSpacing: '0.01em',
        },
        m_body2: {
            fontFamily: 'montserrat',
            fontWeight: 500,
            fontSize: '0.875rem',
            lineHeight: 1.6,
            letterSpacing: '0.01em',
        },
        m_caption: {
            fontFamily: 'montserrat',
            fontWeight: 600,
            fontSize: '0.75rem',
            lineHeight: 1.4,
            letterSpacing: '0.02em',
        },
        m_micro: {
            fontFamily: 'montserrat',
            fontWeight: 600,
            fontSize: '0.625rem',
            lineHeight: 1.3,
            letterSpacing: '0.03em',
        },
        m_tiny: {
            fontFamily: 'montserrat',
            fontWeight: 600,
            fontSize: '0.5rem',
            lineHeight: 1.3,
            letterSpacing: '0.03em',
        },
        m_nano: {
            fontFamily: 'montserrat',
            fontWeight: 600,
            fontSize: '0.4375rem',
            lineHeight: 1.3,
            letterSpacing: '0.03em',
        },
        m_overline: {
            fontFamily: 'montserrat',
            fontWeight: 700,
            fontSize: '0.75rem',
            lineHeight: 1.4,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
        },
        m_overline_lg: {
            fontFamily: 'montserrat',
            fontWeight: 800,
            fontSize: '0.9375rem',
            lineHeight: 1.4,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
        },
        m_badge: {
            fontFamily: 'montserrat',
            fontWeight: 800,
            fontSize: '0.6875rem',
            lineHeight: 1.2,
            letterSpacing: '0.02em',
        },
        m_dialog_title: {
            fontFamily: 'montserrat',
            fontWeight: 600,
            fontSize: '1.25rem',
            lineHeight: 1.6,
            letterSpacing: '0.0075em',
            color: 'rgb(250, 231, 181)', // primary.main
        },
        m_dialog_content_text: {
            fontFamily: 'montserrat',
            fontWeight: 400,
            fontSize: '0.875rem',
            lineHeight: 1.43,
            letterSpacing: '0.01071em',
            color: 'rgb(211, 211, 211)', // secondary.main
        },
    },
});

// Black & white admin console theme (Cloudflare-style: white surfaces, near-black
// text, thin light-gray borders). Monochrome — no color accents.
const WhiteTheme = createTheme({
    palette: {
        type: 'light',
        primary: {
            main: 'rgb(23, 23, 23)', // near black
            light: 'rgb(64, 64, 64)',
            dark: 'rgb(0, 0, 0)',
            bright: 'rgb(96, 96, 96)',
        },
        secondary: {
            main: 'rgb(110, 110, 115)', // mid gray
            light: 'rgb(150, 150, 155)',
            dark: 'rgb(70, 70, 75)',
        },
        // typography
        text: {
            primary: 'rgb(23, 23, 23)',
            secondary: 'rgba(23, 23, 23, 0.62)',
            disabled: 'rgba(23, 23, 23, 0.38)',
        },
        // background
        background: {
            paper: 'rgb(255, 255, 255)', // white cards
            default: 'rgb(251, 251, 251)', // subtle off-white page
        },
        // divider
        divider: 'rgb(226, 226, 226)', // thin light gray border
        // buttons
        action: {
            active: 'rgba(23, 23, 23, 0.54)',
            hover: 'rgba(23, 23, 23, 0.04)',
            hoverOpacity: 0.04,
            selected: 'rgba(23, 23, 23, 0.08)',
            selectedOpacity: 0.08,
            disabled: 'rgba(23, 23, 23, 0.26)',
            disabledBackground: 'rgba(23, 23, 23, 0.12)',
            disabledOpacity: 0.38,
            focus: 'rgba(23, 23, 23, 0.12)',
            focusOpacity: 0.12,
            activatedOpacity: 0.12,
            activeChannel: '0 0 0',
            selectedChannel: '0 0 0',
        },
        markdown: {
            code: 'rgba(23, 23, 23, 0.05)',
            th: 'rgb(23, 23, 23)',
            td: 'rgba(23, 23, 23, 0.25)',
        },
        scrollbar: {
            thumb: '#8b8b8b',
            thumbHover: '#6e6e6e',
        },
    },
    components: {
        MuiTypography: {
            variants: [
                {
                    props: { variant: 'm_overline' },
                    style: { textTransform: 'uppercase' },
                },
                {
                    props: { variant: 'm_overline_lg' },
                    style: { textTransform: 'uppercase' },
                },
            ],
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    backgroundColor: 'rgb(23, 23, 23)',
                    color: 'rgb(255, 255, 255)',
                    border: '1px solid rgb(23, 23, 23)',
                    fontSize: '12px',
                },
                arrow: {
                    color: 'rgb(23, 23, 23)',
                },
            },
        },
        MuiTextField: {
            defaultProps: {
                variant: 'outlined',
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    '& fieldset': {
                        borderColor: 'rgb(226, 226, 226)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgb(23, 23, 23)',
                    },
                    '&.Mui-focused fieldset': {
                        borderColor: 'rgb(23, 23, 23) !important',
                    },
                    '&.Mui-error fieldset': {
                        borderColor: 'rgb(23, 23, 23) !important',
                    },
                },
                input: {
                    color: 'rgb(23, 23, 23)',
                },
            },
        },
        MuiFormLabel: {
            styleOverrides: {
                root: {
                    color: 'rgb(110, 110, 115)',
                    '&.Mui-focused': {
                        color: 'rgb(23, 23, 23)',
                    },
                    '&.Mui-error': {
                        color: 'rgb(23, 23, 23)',
                    },
                },
            },
        },
        MuiAutocomplete: {
            defaultProps: {
                PopperComponent: PopperComponent,
                ListboxComponent: ListboxComponent,
            },
            styleOverrides: {},
        },
    },
    typography: {
        ...DarkTheme.typography,
        m_dialog_title: {
            ...DarkTheme.typography.m_dialog_title,
            color: 'rgb(23, 23, 23)', // primary.main
        },
        m_dialog_content_text: {
            ...DarkTheme.typography.m_dialog_content_text,
            color: 'rgb(110, 110, 115)', // secondary.main
        },
    },
});

export { LightTheme, DarkTheme, WhiteTheme };
